
































































import { Component, Inject, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums/Data
import { BasketTypes } from "@/data/constants";

// Types
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IShopProviderMethods } from "@/models/providers/basket/shop";
import BasketFields from "@/components/app/global/baskets/basket/shared/basketFields.vue";
import { IBasketProduct } from "@/models/baskets";

@Component({
  components: {
    BasketItems: () =>
      import(
        "@/components/app/global/baskets/basket/basketView/basketItems.vue"
      ),
    ContinueShoppingButton: () =>
      import(
        "@/components/app/global/baskets/basket/shared/continueShoppingButton.vue"
      ),
    BasketControl: () =>
      import(
        "@/components/app/global/baskets/basket/basketView/basketControl.vue"
      ),
    BasketFields,
    "basket-provision-warnings": () =>
      import(
        "@/components/app/global/baskets/basket/shared/provisionWarnings.vue"
      )
  }
})
export default class BasketView extends Mixins(HasValidation, Context) {
  public declare $refs: {
    basketFields: BasketFields;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Inject - Basket provider

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionData!: () => IProvisionProviderData;

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionMethods!: IProvisionProviderMethods;

  @Inject({ from: "shopProviderMethods" })
  public readonly $shopMethods!: IShopProviderMethods;

  // Variables

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  // Computed

  public get basketData() {
    return this.$basketData();
  }

  public get provisionData() {
    return this.$provisionData();
  }

  // Life cycle methods

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      if (this.basketData.basketId === BasketTypes.NEW) {
        this.$shopMethods.goToShop();
        return;
      }
      await this.$provisionMethods.checkConfiguration(this.basketData.basketId);
      this.requestError = false;
    } catch (error) {
      if (
        this.$store.getters["api/is403"](error) ||
        this.$store.getters["api/is401"](error)
      ) {
        this.$basketMethods.forceBasketReset();
      }
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public goToProductView(basketProduct: IBasketProduct) {
    this.$shopMethods.goToProductView({ basketProductId: basketProduct.id });
  }

  public async validateBeforeSubmit() {
    try {
      this.isProcessing = true;
      this.$basketMethods.set("isProcessing", true);

      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      if (
        !this.isGuest &&
        this.provisionData.hasProvisioning &&
        !this.$_.isEmpty(this.provisionData.provisionErrors)
      )
        throw this.$t("_sentence.basket.items_have_invalid_data");

      await this.$refs.basketFields?.saveBasketFieldsData();

      if (this.isGuest) {
        this.isProcessing = false;
        this.$basketMethods.set("isProcessing", false);
        return this.$shopMethods.goToAuthenticate();
      }

      this.$shopMethods.goToCheckout();
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
      this.$basketMethods.set("isProcessing", false);
    }
  }

  public get basket() {
    return this.basketData.basket;
  }
}
